import { useEffect, useState } from "react";
// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import orderTableData from "layouts/orderManagement/data/orderTableData";
import add from "assets/images/add.png";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  ClickAwayListener,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Popper,
  Select,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import DateRange from "components/DatePicker";
import moment from "moment";
import { DayPicker } from "react-day-picker";
import MDInput from "components/MDInput";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ClearIcon from "@mui/icons-material/Clear";
import { OrderType } from "helpers/constants/enum";
import { useGetOrderMutation } from "services/order";
import MDBadge from "components/MDBadge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import WarnModal from "components/DeleteModal";
import { useDeleteOrderMutation } from "services/order";
import Loader from "helpers/constants/Loader";
import ShowToast from "components/MDToast";
import Pagination from "components/Pagination";
import { useUpdateOrderStatusMutation } from "services/order";
import { isValidInput } from "utils/validations";
import SearchBar from "components/SearchBar";
import { getFromStorage } from "helpers";
import { STORAGE_KEYS } from "helpers";
import useTranslation from "hooks/Translation";

const OrderManagement = () => {
  const t = useTranslation();
  const currency = getFromStorage(STORAGE_KEYS.currency);
  const navigate = useNavigate();
  const { state } = useLocation();
  const [deleteOrder] = useDeleteOrderMutation();
  const [orderStatus] = useUpdateOrderStatusMutation();
  const [getOrderListing, { isLoading }] = useGetOrderMutation();
  const [month, setMonth] = useState(new Date());
  const [range, setRange] = useState({
    from: undefined,
    to: undefined,
  });
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(false);
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [tabValue, setTabValue] = useState(0);
  const [orderData, setOrderData] = useState([]);
  const [selectedId, setSelected] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const handleDeleteOrder = async (id) => {
    try {
      const response = await deleteOrder({ id: id }).unwrap();
      if (response?.status === 200) {
        ShowToast("Order deleted successfully", "success");
        getData();
        setDeleteModalOpen(false);
      }
    } catch (error) {
      console.log(error, "errror");
      ShowToast(error?.data?.message || "", "error");
    }
  };

  const Job = ({ title, description }) => (
    <MDBox lineHeight={1} textAlign="left" sx={{ minWidth: "110px" }}>
      <MDTypography display="block" variant="caption" fontWeight="medium">
        {title}
      </MDTypography>
      <MDTypography variant="caption">{description}</MDTypography>
    </MDBox>
  );

  const Name = ({ title, description }) => (
    <MDBox lineHeight={1} textAlign="left" sx={{ minWidth: "140px" }}>
      <MDTypography display="block" variant="caption" fontWeight="medium">
        {title}
      </MDTypography>
      <MDTypography variant="caption">{description}</MDTypography>
    </MDBox>
  );

  const totalPages = Math.ceil(totalRecords / limit);

  const getData = async (page) => {
    try {
      let body = {
        start: page,
        length: 10,
        order_type:
          tabValue === 0
            ? OrderType.ONGOING
            : tabValue === 1
            ? OrderType.COMPLETED
            : tabValue === 2
            ? OrderType.CANCELLED
            : 1,
        search: debouncedSearchTerm,
        ...(range?.from
          ? { start_date: moment(range?.from).startOf("day") }
          : {}),
        ...(range?.to ? { end_date: moment(range?.to).startOf("day") } : {}),
      };

      const res = await getOrderListing({ body: body }).unwrap();
      if (res?.status === 200) {
        setOrderData(res?.data?.response_object || []);
        setTotalRecords(res?.data?.total_records);
      } else {
        console.log(res?.message, "error");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleRequestStatus = async (status, id) => {
    const currentDate = new Date().toISOString(); // Get the current date in ISO format
    const statusValue = status.target.value;
    try {
      const body = {
        order_status: statusValue,
        ...(statusValue === "2" ? { in_progress_date: currentDate } : {}),
        ...(statusValue === "3" ? { shipping_date: currentDate } : {}),
        ...(statusValue === "4" ? { delivered_date: currentDate } : {}),
      };

      const response = await orderStatus({
        body,
        id: id,
      }).unwrap();
      if (response?.status === 200) {
        const updatedData = orderData?.map((x) => {
          if (x.id === id) {
            return {
              ...x,
              order_status: Number(status.target.value),
            };
          }
          return x;
        });
        setOrderData(updatedData);
        ShowToast("Status updated successfully", "success");
      } else {
        ShowToast("Please try again", "error");
      }
    } catch {
      ShowToast("Something went wrong", "error");
    }
  };

  const columns = [
    { Header: t.main.orderId, accessor: "Id", align: "left" },
    { Header: t.main.customerName, accessor: "name", align: "left" },
    { Header: t.main.contact, accessor: "contact", align: "left" },
    // { Header: "Quantity", accessor: "quantity", align: "left" },
    { Header: t.main.subTotal, accessor: "price", align: "left" },
    { Header: t.main.totalCostPay, accessor: "cost", align: "left" },
    {
      Header: t.main.paymentStatus,
      accessor: "payment",
      align: "left",
      width: "15%",
    },
    { Header: t.main.orderStatus, accessor: "request", align: "center" },
    { Header: t.main.actions, accessor: "action", align: "center" },
  ];

  const rows = orderData?.map((itx) => ({
    Id: <Name title={`#${itx?.order_track_id}` || ""} />,
    name: <Name title={itx?.user || "--"} />,
    contact: <Job title={`${itx?.country_code}-${itx?.phone_no}` || "--"} />,
    // quantity: <Job title="10kg" />,
    price: (
      <Job
        title={`${currency === "USD" ? "USD" : "AED"} ${itx?.sub_total || "0"}`}
      />
    ),
    cost: (
      <Name
        title={`${currency === "USD" ? "USD" : "AED"} ${
          itx?.total_cost_pay || "0"
        }`}
      />
    ),
    payment: (
      <MDBox sx={{ minWidth: "140px" }}>
        <MDBadge
          badgeContent="Completed"
          color="success"
          variant="gradient"
          size="md"
        />
      </MDBox>
    ),
    request: (
      <MDBox sx={{ width: 120 }}>
        {tabValue === 2 ? (
          <MDBox sx={{ minWidth: "140px" }}>
            <MDBadge
              badgeContent="Cancelled"
              color="error"
              variant="gradient"
              size="md"
            />
          </MDBox>
        ) : tabValue === 1 ? (
          <MDBox sx={{ minWidth: "140px" }}>
            <MDBadge
              badgeContent="Delivered"
              color="success"
              variant="gradient"
              size="md"
            />
          </MDBox>
        ) : (
          <FormControl fullWidth>
            <Select
              className="select_div"
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={String(itx?.order_status)}
              displayEmpty
              disabled={tabValue === 1}
              onChange={(val) => handleRequestStatus(val, itx?.id)}
              sx={{
                border: "1px solid grey", // Add a custom border color
                borderRadius: "6px", // Optional: Add border radius for styling
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "grey", // Adjust border color for the outlined variant
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "grey", // Border color on hover
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "grey", // Border color when focused
                },
              }}
            >
              <MenuItem
                value={"1"}
                disabled={
                  itx?.order_status !== "1" && itx?.order_status !== "2"
                }
              >
                Order Placed
              </MenuItem>
              <MenuItem
                value={"2"}
                disabled={itx?.order_status < "1" || itx?.order_status > "2"}
              >
                In-Progress
              </MenuItem>
              <MenuItem
                value={"3"}
                disabled={itx?.order_status < "2" || itx?.order_status > "3"}
              >
                Shipped
              </MenuItem>
              <MenuItem value={"4"} disabled={itx?.order_status < "3"}>
                Delivered
              </MenuItem>
            </Select>
          </FormControl>
        )}
      </MDBox>
    ),
    action: (
      <Box className="table_actions">
        <IconButton
          size="small"
          onClick={() =>
            navigate(`/manage-orders/details/${itx?.id}`, {
              state: { value: tabValue },
            })
          }
        >
          <VisibilityIcon />
        </IconButton>

        <IconButton
          size="small"
          onClick={() => {
            setDeleteModalOpen(true);
            setSelected(itx?.id);
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    ),
  }));

  useEffect(() => {
    getData(page);
  }, [tabValue, page, debouncedSearchTerm, range?.from, range?.to]);

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  const handleSelect = (selectedRange) => {
    if (selectedRange === undefined) {
      setRange({ from: undefined, to: undefined });
    } else {
      setRange(selectedRange);
      if (
        selectedRange.from !== undefined &&
        selectedRange.to !== undefined &&
        selectedRange.from !== selectedRange.to
      ) {
        setOpen(false);
      }
    }
  };

  const handleMonthChange = (newMonth) => {
    setMonth(newMonth);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const getFormattedRange = () => {
    if (range?.from && range?.to) {
      return `${moment(range.from).format("MM/DD/YYYY")} - ${moment(
        range.to
      ).format("MM/DD/YYYY")}`;
    } else if (range?.from) {
      return moment(range.from).format("MM/DD/YYYY");
    }
    return "";
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSetTabValue = (event, newValue) => setTabValue(newValue);

  useEffect(() => {
    if (state) {
      setTabValue(state?.value);
    }
  }, []);

  return (
    <DashboardLayout>
      <Loader loading={isLoading} />
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                // variant="gradient"
                bgColor="theme-1"
                borderRadius="lg"
                coloredShadow="info"
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <MDTypography variant="h6" color="white">
                  {t.main.ordersTable}
                </MDTypography>
              </MDBox>

              <MDBox pt={2} pl={2} pr={2}>
                <div style={{ display: "flex", gap: 10 }}>
                  <SearchBar
                    value={searchTerm}
                    placeholder={"Order ID"}
                    searchTerm={searchTerm}
                    onCross={() => setSearchTerm("")}
                    setDebouncedSearchTerm={setDebouncedSearchTerm}
                    onChange={(val) => {
                      if (isValidInput(val.target.value)) {
                        setSearchTerm(val.target.value);
                      }
                    }}
                  />
                  <div className="control_group" style={{ width: "250px" }}>
                    <MDInput
                      // label="Select Date Range"
                      variant="outlined"
                      hiddenLabel
                      className="text_field-range"
                      fullWidth
                      onClick={handleClick}
                      value={getFormattedRange()}
                      placeholder={t.main.selectDateRange}
                      InputProps={{
                        readOnly: true,
                        endAdornment: range?.from ? (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => {
                                setRange({
                                  from: undefined,
                                  to: undefined,
                                });
                              }}
                            >
                              <ClearIcon />
                            </IconButton>
                          </InputAdornment>
                        ) : (
                          <InputAdornment position="end">
                            <IconButton>
                              <CalendarTodayIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                  <Popper
                    open={open}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    disablePortal
                    style={{
                      position: "relative",
                      zIndex: 100,
                    }}
                  >
                    <ClickAwayListener onClickAway={handleClose}>
                      <Paper
                        elevation={3}
                        style={{ position: "relative", zIndex: 1 }}
                      >
                        <div
                          style={{
                            padding: 16,
                            borderRadius: 10,
                          }}
                        >
                          <DayPicker
                            className="react-day-picker"
                            mode="range"
                            selected={range}
                            onSelect={handleSelect}
                            // numberOfMonths={10}
                            month={month}
                            onMonthChange={handleMonthChange}
                            // styles={{
                            //   months: { display: "flex", gap: "16px" },
                            //   month: { margin: 0 },
                            // }}
                          />
                        </div>
                      </Paper>
                    </ClickAwayListener>
                  </Popper>
                </div>
                <Grid container spacing={2} pt={2}>
                  <Grid item xs={12} md={4} lg={4}>
                    <Tabs
                      value={tabValue}
                      onChange={handleSetTabValue}
                      orientation={tabsOrientation}
                      className="custom_tabs_links"
                    >
                      <Tab
                        sx={{ fontWeight: "500" }}
                        label={t.main.onGoing}
                        {...a11yProps(0)}
                      />
                      <Tab
                        sx={{ fontWeight: "500" }}
                        label={t.main.completed}
                        {...a11yProps(1)}
                      />
                      <Tab
                        sx={{ fontWeight: "500" }}
                        label={t.main.cancelled}
                        {...a11yProps(2)}
                      />
                    </Tabs>
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox pt={3} pl={2} pr={2}>
                {rows.length > 0 ? (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                ) : (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={5}
                  >
                    <MDTypography variant="h6" color="text">
                      {t.main.noData}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
              {orderData.length > 0 && (
                <MDBox p={2}>
                  <Pagination
                    setPage={setPage}
                    module={orderData}
                    page={page}
                    onPageChange={setPage}
                    totalPages={totalPages}
                    limit={limit}
                    setLimit={setLimit}
                  />
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
      <WarnModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        handleDelete={() => handleDeleteOrder(selectedId)}
        name="Order"
      />
    </DashboardLayout>
  );
};

export default OrderManagement;
